// @safetyhub/report-composer — 작성폼 표(formTable) 헤더·열 모델 정규화 (순수 함수)
//
// 헤더=일반 셀, columns=구조 축만 (결정: docs/decisions/2026-07-13-formtable-header-cell-model.md).
// 구모델(columns[].label/group 파생 thead + 표 수준 headerStyle)을 신모델(헤더 고정 행 +
// colSpan/rowSpan 병합 + 셀별 style 박제)로 변환한다. 렌더(composerWidgetTreeRender)와
// admin 에디터(위젯 로드/편집) **양쪽이 이 함수 하나를 통과**한다 — legacy fallback 이중 구현 금지.

import type {
  ComposerTextStyle,
  FormTableColumn,
  FormTableFixedCell,
  FormTableRow,
  FormTableWidget,
  TableCellStyle,
} from '@safetyhub/shared';
import type { ComposerTextStyleDefaults } from './composerWidgetTreeRender';

/** 작성폼 표 헤더 기본 = 구 SMS 대화면 작성 테이블 th(report-xl-text 기준, medium/gray-500). */
export const COMPOSER_FORM_TABLE_HEADER_DEFAULT_STYLE: ComposerTextStyleDefaults = {
  fontSize: 14,
  fontWeight: 500,
  color: '#6b7280',
};

/** 구 thead 헤더 셀 배경(FORM_TABLE_TH_STYLE) — 변환 시 헤더 셀 style로 박제. */
export const FORM_TABLE_HEADER_CELL_BACKGROUND = '#f9fafb';

/** legacy column.align(left/center/right) → 셀 contentAlignH. left=기본(start)이라 박제 생략. */
const COLUMN_ALIGN_TO_H: Record<'center' | 'right', 'center' | 'end'> = {
  center: 'center',
  right: 'end',
};

/** 변환 필요 여부 — 비공백 label 또는 group이 있는 열이 하나라도 있으면 구모델. */
function hasLegacyHeader(ft: FormTableWidget): boolean {
  return ft.columns.some(
    (c) => (typeof c.label === 'string' && c.label.trim() !== '') || !!c.group,
  );
}

/** widthPct(구) 잔존 여부 — 하나라도 있으면 width로 변환 필요. */
function hasLegacyWidthPct(ft: FormTableWidget): boolean {
  return ft.columns.some((c) => c.widthPct !== undefined);
}

/** 열의 widthPct → width('{n}%') 변환. width가 이미 있으면 우선(widthPct 키만 제거). */
function convertColumnWidth(c: FormTableColumn): FormTableColumn {
  if (c.widthPct === undefined) return c;
  const next: FormTableColumn = { ...c };
  delete next.widthPct;
  if (next.width === undefined) next.width = `${c.widthPct}%`;
  return next;
}

/** headerStyle×기본값 합성 → 헤더 셀 박제 style(배경 + 폰트). 구 thStyle 시각과 동일. */
function bakedHeaderCellStyle(headerStyle: ComposerTextStyle | undefined): TableCellStyle {
  const hs = headerStyle ?? {};
  const d = COMPOSER_FORM_TABLE_HEADER_DEFAULT_STYLE;
  const style: TableCellStyle = {
    background: FORM_TABLE_HEADER_CELL_BACKGROUND,
    fontSize: hs.fontSize ?? d.fontSize,
    fontWeight: hs.fontWeight ?? d.fontWeight,
    color: hs.color ?? d.color,
  };
  return style;
}

/**
 * rowSpan 병합 구간에 걸린 고정 행 id 집합 — 병합 셀(rowSpan>1)을 가진 행(owner) + 그 병합이 아래로
 * 덮는 행(covered). 행 visibleWhen과 상호배타 판정의 단일 소스(정규화 strip + admin 에디터 입력 비활성
 * 양쪽이 공유). repeat 행을 만나면 이월을 비운다(병합은 인접 고정 행 전용 — 렌더 점유 맵 규약 동형).
 */
export function ftRowSpanInvolvedRowIds(ft: FormTableWidget): Set<string> {
  const involved = new Set<string>();
  let remaining = 0; // 위 병합이 아래로 덮을 잔여 행수(열 무관 최대치 — 행 단위 판정엔 충분)
  for (const row of ft.rows) {
    if (row.type !== 'fixed') {
      remaining = 0;
      continue;
    }
    if (remaining > 0) {
      involved.add(row.id); // covered
      remaining -= 1;
    }
    for (const cell of row.cells) {
      const rs = Math.max(1, cell.rowSpan ?? 1);
      if (rs > 1) {
        involved.add(row.id); // owner
        remaining = Math.max(remaining, rs - 1);
      }
    }
  }
  return involved;
}

/**
 * rowSpan 병합 구간에 걸린 행의 visibleWhen을 제거한다(상호배타 단일 가드 — 결정
 * 2026-08-17-formtable-row-visiblewhen). 에디터가 양방향 차단하므로 정상 경로에선 위반 데이터가
 * 없고, 위반(구버전 저장본/외부 조작) 시에만 여기서 strip되어 렌더 격자가 깨지지 않는다.
 * 위반 없으면 입력 그대로 반환(idempotent — 참조 동일).
 */
function stripRowSpanConflictVisibleWhen(ft: FormTableWidget): FormTableWidget {
  const hasAnyVisibleWhen = ft.rows.some((r) => r.type === 'fixed' && r.visibleWhen !== undefined);
  if (!hasAnyVisibleWhen) return ft;
  const involved = ftRowSpanInvolvedRowIds(ft);
  const violates = (r: FormTableRow): r is Extract<FormTableRow, { type: 'fixed' }> =>
    r.type === 'fixed' && r.visibleWhen !== undefined && involved.has(r.id);
  if (!ft.rows.some(violates)) return ft;
  const rows = ft.rows.map((row): FormTableRow => {
    if (!violates(row)) return row;
    const next = { ...row };
    delete next.visibleWhen;
    return next;
  });
  return { ...ft, rows };
}

/**
 * 구모델 formTable을 신모델로 정규화한다(순수·결정적 — 같은 입력이면 같은 출력, 헤더 행 id도
 * `${ft.id}__hdr1/2`로 고정이라 렌더 중 즉석 정규화와 트리에 기록된 정규화가 항상 일치).
 * 마지막에 rowSpan 병합 구간과 충돌하는 행 visibleWhen을 strip한다(상호배타 단일 가드).
 *
 * - columns에 비공백 label 또는 group이 있으면: 헤더 고정 행 1~2개를 rows 맨 앞에 생성.
 *   group 있으면 2행(1행=group 인접 열 colSpan 병합 셀 + group 없는 열 rowSpan=2 / 2행=group
 *   있는 열의 label 셀), 없으면 1행(label 셀). 구 thead 파생 렌더와 시각 결과 동일.
 * - 헤더 셀엔 구 헤더 시각(배경 + headerStyle×기본 폰트 합성 + 중앙 정렬)을 셀 style/contentAlignH로 박제.
 * - column.align → 그 열의 기존 고정 행 셀 contentAlignH로 박제(셀에 이미 정렬 지정 시 셀 우선.
 *   left는 기본과 동일이라 박제 생략). 헤더 셀은 구 thead 관례대로 항상 중앙.
 * - 변환 후 columns는 id/width만 — 단 **반복 행이 참조하는 열의 align은 보존**(반복 행 셀은
 *   렌더 fallback으로 column.align을 계속 읽는다 — 반복 셀엔 셀별 정렬 축이 없음). headerStyle 제거.
 * - column.widthPct(구)는 `width: '{n}%'`로 변환(width 이미 있으면 우선). 헤더가 신형이라도
 *   widthPct가 잔존하면 열 폭만 변환하고 rows는 그대로 둔다.
 * - 이미 신형(label/group 없음 + widthPct 없음)이면 입력 그대로 반환(idempotent — 참조 동일).
 */
export function normalizeFormTable(ft: FormTableWidget): FormTableWidget {
  return stripRowSpanConflictVisibleWhen(normalizeLegacyHeaderAndWidth(ft));
}

/** 구모델(헤더 파생/widthPct) → 신모델 변환부 — normalizeFormTable 본체(strip 전 단계). */
function normalizeLegacyHeaderAndWidth(ft: FormTableWidget): FormTableWidget {
  const legacyHeader = hasLegacyHeader(ft);
  const legacyWidth = hasLegacyWidthPct(ft);
  if (!legacyHeader && !legacyWidth) return ft;

  // 헤더는 신형이나 widthPct만 잔존 — 열 폭만 변환하고 rows는 불변(idempotent 참조 최소 변경).
  if (!legacyHeader) {
    const columns = ft.columns.map(convertColumnWidth);
    return { ...ft, columns };
  }

  const cols = ft.columns;
  const has2Tier = cols.some((c) => c.group);
  const headerStyle = bakedHeaderCellStyle(ft.headerStyle);

  // ── 헤더 고정 행 생성(구 buildFormTableTopCells + thead 렌더와 동일 병합 규칙) ──
  const topCells: FormTableFixedCell[] = [];
  let i = 0;
  while (i < cols.length) {
    const g = cols[i].group;
    if (g) {
      let j = i;
      while (j < cols.length && cols[j].group === g) j += 1;
      topCells.push({
        columnId: cols[i].id,
        ...(j - i > 1 ? { colSpan: j - i } : {}),
        text: g,
        contentAlignH: 'center',
        style: headerStyle,
      });
      i = j;
    } else {
      topCells.push({
        columnId: cols[i].id,
        ...(has2Tier ? { rowSpan: 2 } : {}),
        text: cols[i].label ?? '',
        contentAlignH: 'center',
        style: headerStyle,
      });
      i += 1;
    }
  }
  const headerRows: FormTableRow[] = [
    { type: 'fixed', id: `${ft.id}__hdr1`, cells: topCells },
  ];
  if (has2Tier) {
    headerRows.push({
      type: 'fixed',
      id: `${ft.id}__hdr2`,
      cells: cols
        .filter((c) => c.group)
        .map(
          (c): FormTableFixedCell => ({
            columnId: c.id,
            text: c.label ?? '',
            contentAlignH: 'center',
            style: headerStyle,
          }),
        ),
    });
  }

  // ── 기존 고정 행 셀에 column.align 박제(셀 지정 우선, left=기본이라 생략) ──
  const colAlignH = new Map<string, 'center' | 'end'>();
  for (const c of cols) {
    if (c.align === 'center' || c.align === 'right') colAlignH.set(c.id, COLUMN_ALIGN_TO_H[c.align]);
  }
  const bodyRows = ft.rows.map((row): FormTableRow => {
    if (row.type !== 'fixed' || colAlignH.size === 0) return row;
    let changed = false;
    const cells = row.cells.map((cell): FormTableFixedCell => {
      const h = colAlignH.get(cell.columnId);
      if (!h || cell.contentAlignH || cell.align) return cell;
      changed = true;
      return { ...cell, contentAlignH: h };
    });
    return changed ? { ...row, cells } : row;
  });

  // ── columns = 구조 축만(id/widthPct). 반복 행이 참조하는 열의 align만 fallback으로 보존 ──
  const repeatColumnIds = new Set<string>();
  for (const row of ft.rows) {
    if (row.type === 'repeat') for (const cf of row.cellFields) repeatColumnIds.add(cf.columnId);
  }
  const columns = cols.map((c): FormTableColumn => {
    const next: FormTableColumn = { id: c.id };
    if (c.width !== undefined) next.width = c.width;
    else if (c.widthPct !== undefined) next.width = `${c.widthPct}%`;
    if (c.align !== undefined && repeatColumnIds.has(c.id)) next.align = c.align;
    return next;
  });

  const next: FormTableWidget = { ...ft, columns, rows: [...headerRows, ...bodyRows] };
  delete next.headerStyle; // 헤더 셀 style로 박제 완료 — 표 수준 축 소멸(@deprecated)
  return next;
}
